<template>
  <div class="video-wrapper" :style="cssVars">
    <template v-if="!isPlaying && lazy">
      <button class="video-poster" :style="posterStyle" @click="play" @keyup.enter.space="play" @contextmenu.prevent
        aria-label="Reproducir video" >
        <div class="video-poster-overlay">
          <div class="play-button" aria-hidden v-if="!ocultarBtn"></div>
        </div>
      </button>
    </template>

    <!-- Si el vídeo es del mismo origen usamos el elemento nativo <video> con controles personalizados -->
  <div v-if="useNativeVideo && (isPlaying || !lazy)" class="native-video-container" ref="containerEl">
      <video
        ref="videoEl"
        :src="videoSrc"
        v-bind:poster="generatedPoster || undefined"
        playsinline
        disablepictureinpicture
        disableremoteplayback
        @contextmenu.prevent="onVideoContextMenu"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @play="onPlay"
        @pause="onPause"
        :preload="isPlaying ? 'auto' : 'metadata'"></video>

      <div class="video-controls" role="group" aria-label="Controles de vídeo">
        <button class="btn-play" @click="togglePlay" :aria-pressed="isVideoPlaying" aria-label="Reproducir/Pausar">
          <span v-if="!isVideoPlaying">▶</span>
          <span v-else>▮▮</span>
        </button>

        <div class="progress-wrap">
          <input class="progress" type="range" min="0" :max="duration" step="0.01" v-model.number="currentTime" @input="onSeek">
        </div>

        <div class="time">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</div>
        <button class="btn-fullscreen" @click="toggleFullscreen" :aria-pressed="isFullscreen" aria-label="Pantalla completa">
          <span v-if="!isFullscreen">⛶</span>
          <span v-else>⤢</span>
        </button>
      </div>
    </div>

    <!-- Para embeds externos (YouTube, Vimeo, etc.) usamos iframe -->
    <iframe v-else-if="isPlaying || !lazy" :src="iframeSrc" frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen
      loading="lazy"  referrerpolicy="strict-origin-when-cross-origin"></iframe>
  

    <div v-if="!embedUrl && (!isPlaying && lazy)" class="video-empty">URL inválida</div>
  </div>
</template>

<script setup>

import PlayMovieIcon from '@images/pages/portada_video.png'
import { nextTick, onMounted, onBeforeUnmount } from 'vue'
const props = defineProps({
  url: { type: String, required: true },
  // formato esperado "16/9", "4/3" o cualquier valor válido para aspect-ratio
  ratio: { type: String, default: '16/9' },
  // borde redondeado (ej: "12px" o número 12 que se transformará a "12px")
  rounded: { type: [String, Number], default: '12px' },
  // lazy load: si true muestra poster y carga iframe al click
  lazy: { type: Boolean, default: true },
  // poster URL opcional
  poster: { type: String, default: '' },
  // ocultar botón de play en modo lazy
  ocultarBtn: { type: Boolean, default: false },
});

const isPlaying = ref(!props.lazy);

// calcula el valor CSS para aspect-ratio y padding-top fallback
const { aspectRatioValue, paddingTopValue } = (() => {
  const parts = props.ratio.split('/').map(p => parseFloat(p));
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[0] > 0) {
    const aspectRatioValue = `${parts[0]}/${parts[1]}`; // "16/9"
    const paddingTopValue = `${(parts[1] / parts[0]) * 100}%`; // "56.25%"
    return { aspectRatioValue, paddingTopValue };
  }
  return { aspectRatioValue: props.ratio, paddingTopValue: '56.25%' };
})();

// detectar provider y extraer id
const detect = (url) => {
  try {
    const u = new URL(url, window.location.href);
    const host = u.hostname.replace('www.', '');
    if (host.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return { provider: 'youtube', id: v };
      if (u.pathname.includes('/embed/')) return { provider: 'youtube', id: u.pathname.split('/').pop() };
    }
    if (host === 'youtu.be') {
      const id = u.pathname.replace('/', '');
      return { provider: 'youtube', id };
    }
    if (host.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return { provider: 'vimeo', id };
    }
  } catch (e) {
    // no válido
  }
  return { provider: 'other', id: null };
};

const info = computed(() => detect(props.url));

// URL de embed base (sin autoplay)
const embedUrl = computed(() => {
  if (!props.url) return '';
  if (info.value.provider === 'youtube' && info.value.id) return `https://www.youtube.com/embed/${info.value.id}`;
  if (info.value.provider === 'vimeo' && info.value.id) return `https://player.vimeo.com/video/${info.value.id}`;
  // por defecto usar la url tal cual
  return props.url;
});

// iframe src real (añade autoplay si se está reproduciendo)
const iframeSrc = computed(() => {
  if (!embedUrl.value) return '';
  if (isPlaying.value) {
    const sep = embedUrl.value.includes('?') ? '&' : '?';
    return `${embedUrl.value}${sep}autoplay=1`;
  }
  return embedUrl.value;
});

// origen de la API (variable de entorno) y del recurso de vídeo
const apiOrigin = (() => {
  try {
    return new URL(import.meta.env.VITE_API_URL).origin;
  } catch (e) {
    return '';
  }
})();

const videoOrigin = computed(() => {
  try {
    return new URL(props.url, window.location.href).origin;
  } catch (e) {
    return '';
  }
});

// detectar si `props.url` es en realidad un asset de vídeo importado (p. ej. import video from './v.mp4')
const isUrlVideo = computed(() => {
  const u = props.url;
  if (!u) return false;
  // si es string (lo habitual con Vite) o un objeto con default
  const src = typeof u === 'string' ? u : (u && typeof u === 'object' && typeof u.default === 'string' ? u.default : '');
  if (!src) return false;
  return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(src.split('?')[0]);
});

// fuente real del <video>: si props.url es un asset importado, resolvemos su valor; si no, usamos props.url tal cual
const videoSrc = computed(() => {
  const u = props.url;
  if (!u) return '';
  return typeof u === 'string' ? u : (u && u.default) || '';
});

// compara orígenes: si props.url es un asset de vídeo importado (local) o si el origin coincide con VITE_API_URL,
// usaremos elemento <video> nativo
const useNativeVideo = computed(() => {
  if (isUrlVideo.value) return true;
  if (!apiOrigin || !videoOrigin.value) return false;
  return apiOrigin === videoOrigin.value;
});

// poster: si el prop poster existe se usa; si es YouTube generamos thumbnail; si no, vacío
const generatedPoster = computed(() => {
  if (props.poster) return props.poster;
  if (info.value.provider === 'youtube' && info.value.id) return `https://img.youtube.com/vi/${info.value.id}/hqdefault.jpg`;
  // para Vimeo se podría obtener vía API, pero no lo hacemos aquí; devolver cadena vacía para usar fallback CSS
  return '';
});

const cssVars = computed(() => ({
  '--aspect-ratio': aspectRatioValue,
  '--padding-top': paddingTopValue,
  '--rounded': typeof props.rounded === 'number' ? `${props.rounded}px` : props.rounded,
}));

const posterStyle = computed(() => ({
  backgroundImage: generatedPoster.value ? `url('${generatedPoster.value}')` : undefined,
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
}));

async function play() {
  // activar la carga/render del elemento video
  isPlaying.value = true;
  // esperar que el elemento <video> se monte
  await nextTick();
  const el = videoEl.value;
  if (el) {
    // intentar reproducir inmediatamente
    el.play().catch(() => {});
  }
}

function onVideoContextMenu(e) {
  // evitar que el usuario abra el menú contextual y descargue el vídeo desde ahí
  e.preventDefault();
  return false;
}

// Refs y estado para controles personalizados
const videoEl = ref(null);
const containerEl = ref(null);
const currentTime = ref(0);
const duration = ref(0);
const isVideoPlaying = ref(false);
const isFullscreen = ref(false);

function onLoadedMetadata() {
  const el = videoEl.value;
  if (el) duration.value = el.duration || 0;
}

function onTimeUpdate() {
  const el = videoEl.value;
  if (el) currentTime.value = el.currentTime;
}

function onSeek(e) {
  const el = videoEl.value;
  if (el) {
    el.currentTime = Number(e.target.value);
  }
}

function togglePlay() {
  const el = videoEl.value;
  if (!el) {
    // si el elemento no existe aún, activamos la carga (lazy) y el valor isPlaying se encargará
    isPlaying.value = true;
    return;
  }
  if (el.paused) {
    el.play().catch(() => {});
  } else {
    el.pause();
  }
}

function onPlay() {
  isVideoPlaying.value = true;
}

function onPause() {
  isVideoPlaying.value = false;
}

function onFullscreenChange() {
  const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
  isFullscreen.value = !!fsEl;
}

function requestFullscreen(el) {
  if (!el) return Promise.resolve();
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  if (el.mozRequestFullScreen) return el.mozRequestFullScreen();
  if (el.msRequestFullscreen) return el.msRequestFullscreen();
  return Promise.resolve();
}

function exitFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  if (document.mozCancelFullScreen) return document.mozCancelFullScreen();
  if (document.msExitFullscreen) return document.msExitFullscreen();
  return Promise.resolve();
}

async function toggleFullscreen() {
  try {
    if (!isFullscreen.value) {
      // prefer container, fall back to video element
      await requestFullscreen(containerEl.value || videoEl.value);
    } else {
      await exitFullscreen();
    }
  } catch (e) {
    // ignore
  }
}

function formatTime(sec) {
  if (!sec || !isFinite(sec)) return '0:00';
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  const m = Math.floor(sec / 60);
  return `${m}:${s}`;
}

onMounted(() => {
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  document.addEventListener('mozfullscreenchange', onFullscreenChange);
  document.addEventListener('MSFullscreenChange', onFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
  document.removeEventListener('mozfullscreenchange', onFullscreenChange);
  document.removeEventListener('MSFullscreenChange', onFullscreenChange);
});
</script>

<style lang="scss" scoped>
@use '@variables/variables' as var;

.video-wrapper {
  width: 100%;
  aspect-ratio: var(--aspect-ratio);
  background: #000;
  border-radius: var(--rounded);
  overflow: hidden;
  display: block;
  position: relative;


  video{
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  .native-video-container {
    position: relative;

    video {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .video-controls {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      gap: .5rem;
      padding: .5rem;
      background: linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.5));
    }

    .btn-play {
      background: transparent;
      border: 0;
      color: var.$white;
      font-size: 1rem;
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .btn-fullscreen {
      background: transparent;
      border: 0;
      color: var.$white;
      font-size: 1rem;
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin-left: .5rem;
    }

    .progress-wrap {
      flex: 1 1 auto;
    }

    .progress {
      appearance: none;
      -webkit-appearance: none;
      width: 100%;
      height: 8px;
      background: rgba(255,255,255,0.15);
      border-radius: 999px;
      outline: none;
    }

    .progress::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px;
      height: 14px;
      background: var.$primary;
      border-radius: 50%;
      box-shadow: 0 0 0 4px rgba(0,0,0,0.2);
      cursor: pointer;
    }

    .progress::-moz-range-thumb {
      width: 14px;
      height: 14px;
      background: var.$primary;
      border-radius: 50%;
      cursor: pointer;
    }

    .time {
      color: var.$white;
      font-size: 0.85rem;
      white-space: nowrap;
      margin-left: .5rem;
    }
  }

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  .video-poster {
    width: 100%;
    height: 100%;
    border: 0;
    padding: 0;
    margin: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.35));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
  }

  .video-poster-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.28));
  }

  .play-button {
    width: 72px;
    height: 72px;
    // border-radius: 50%;
    // background: rgba(255,255,255,0.95);
    box-shadow: 0 6px 18px rgba(0,0,0,0.35);
    position: relative;
    background-image: url('@images/pages/play-movie.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    &:hover{
      box-shadow: 0 8px 24px rgba(0,0,0,0.45);
      transform: scale(1.05);
    }
    
  }

  // .play-button::after {
  //   content: '';
  //   position: absolute;
  //   left: .7rem;
  //   right: 0;
  //   top: 14px;
  //   width: 50px;
  //   height: 50px;
  //   // border-left: 18px solid rgba(0,0,0,0.9);
  //   // border-top: 12px solid transparent;
  //   // border-bottom: 12px solid transparent;
  //   background-image:url('@images/pages/play-movie.png');
  //   background-size:contain;
  //   background-repeat:no-repeat;
  //   background-position:center;

  // }

  .video-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    padding: 1rem;
  }
}

/* Fallback para navegadores que no soportan aspect-ratio */
@supports not (aspect-ratio: 16/9) {
  .video-wrapper {
    aspect-ratio: auto;
    position: relative;
    padding-top: var(--padding-top);
  }

  .video-wrapper iframe,
  .video-wrapper .video-poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>